'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useShallow } from 'zustand/react/shallow';
import { useUploadStore } from '@/features/upload';
import { registerAngleIntake } from '@/features/angle/lib/register-angle-intake';
import { autoCropImageFile } from '@/shared/ui/image-crop';
import {
  pollUploadIntake,
  VALIDATION_POLL_INITIAL_DELAY_MS,
  VALIDATION_POLL_INTERVAL_MS,
  VALIDATION_WAIT_TIMEOUT_MS,
} from '@/features/angle/lib/validation-polling';
import {
  decodeValidation,
  readUploadCompletedPayload,
  type DecodableValidation,
} from '@/features/angle/ui/upload';
import { useCreateConcept } from '@/features/design/concept/hooks/use-concepts';
import { saveDraft } from '@/features/design/render/lib/render-settings-storage';
import { renderErrorBlockReason } from '@/features/design/render/hooks/use-render-error-handler';
import { uploadsService } from '@/shared/api/services/uploads.service';
import { anglesService } from '@/shared/api/services/angles.service';
import { rendersService } from '@/shared/api/services/renders.service';
import { onInstant } from '@/shared/api/websocket/realtime-sync.registry';
import { useLimitErrorPaywall } from '@/shared/hooks/useLimitErrorPaywall';
import { useTokenBalance } from '@/features/subscription/model/subscription.selectors';
import { cacheInvalidation } from '@/shared/lib/cache-invalidation';
import { ApiClientError } from '@/shared/lib/api';
import { localizeUploadError } from '@/shared/lib/upload-error';
import { createLogger } from '@/shared/lib/logger';
import { trackEvent, AnalyticsEvent } from '@/shared/lib/analytics';
import type { FirstRunState, FirstRunStep } from '@/types/profile';
import {
  buildFirstRunRenderRequest,
  FIRST_RUN_SLOW_AFTER_MS,
  FIRST_RUN_WIZARD_VERSION,
  normalizeCustomText,
  resolveInitialStep,
  type FirstRunChoice,
} from '../lib/first-run-logic';
import { findResumableDraftForAngle, loadMainConceptAngle } from '../lib/launch';
import { firstRunService, markFirstRunFinished } from '../model/first-run.query';
import { useFirstRunSession } from '../model/first-run.store';

const logger = createLogger('FirstRunWizard');

export type PhotoStatus =
  | 'idle'
  | 'uploading'
  | 'checking'
  | 'valid'
  | 'rejected'
  | 'technical_error'
  | 'timeout'
  | 'upload_failed';

export interface FirstRunPhoto {
  status: PhotoStatus;
  previewUrl: string | null;
  /** Upload progress 0–100 while uploading. */
  progress: number | null;
  /** The API's own text for a refused photo or a failed upload. */
  message: string | null;
  /** The check has taken longer than FIRST_RUN_SLOW_AFTER_MS. */
  isSlow: boolean;
  /** The failed photo can be sent again as it is (technical error only). */
  canRetry: boolean;
}

/** What is being waited on: a fresh intake, or a view that already exists. */
type Watch =
  | { kind: 'intake'; uploadId: string }
  | { kind: 'angle'; angleId: string };

const IDLE_PHOTO: FirstRunPhoto = {
  status: 'idle',
  previewUrl: null,
  progress: null,
  message: null,
  isSlow: false,
  canRetry: false,
};

function track(event: AnalyticsEvent, params: Record<string, string | number | boolean>) {
  trackEvent(event, { ...params, wizard_version: FIRST_RUN_WIZARD_VERSION });
}

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, ms);
    signal.addEventListener(
      'abort',
      () => {
        clearTimeout(timer);
        reject(new DOMException('Aborted', 'AbortError'));
      },
      { once: true }
    );
  });
}

export function useFirstRunWizard(state: FirstRunState & { project_id: string }) {
  const projectId = state.project_id;
  const router = useRouter();
  const tModal = useTranslations('angle.upload.modal');
  const tAngles = useTranslations('angles');
  const tUpload = useTranslations('fileUpload');
  const routeToPaywall = useLimitErrorPaywall();
  const tokensBalance = useTokenBalance();
  const createConcept = useCreateConcept();
  const uploadSingle = useUploadStore((s) => s.uploadSingle);
  const setHandoff = useFirstRunSession((s) => s.setHandoff);

  const [step, setStep] = useState<FirstRunStep>(() => resolveInitialStep(state));
  const [photo, setPhoto] = useState<FirstRunPhoto>(IDLE_PHOTO);
  const [angleId, setAngleId] = useState<string | null>(null);
  const [watch, setWatch] = useState<Watch | null>(null);
  const [choice, setChoice] = useState<FirstRunChoice | null>(null);
  const [customText, setCustomText] = useState('');
  const [isLaunching, setIsLaunching] = useState(false);
  const [launchError, setLaunchError] = useState<string | null>(null);

  const fileRef = useRef<File | null>(null);
  const checkStartedAtRef = useRef<number | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  // Every photo attempt gets a number; answers for an older attempt are dropped.
  const attemptRef = useRef(0);
  const launchingRef = useRef(false);
  const rawStatusRef = useRef<string>('idle');
  const blobUrlRef = useRef<string | null>(null);

  // Upload progress straight from the uploader's queue item.
  const uploadProgress = useUploadStore(
    useShallow((s) => (s.ui.loading.upload ? (s.queue[0]?.progress ?? 0) : null))
  );
  useEffect(() => {
    if (photo.status === 'uploading' && uploadProgress !== null) {
      setPhoto((p) => ({ ...p, progress: uploadProgress }));
    }
  }, [uploadProgress, photo.status]);

  const copy = useRef({ notSuitable: '', technicalError: '' });
  copy.current = {
    notSuitable: tModal('notSuitableImage'),
    technicalError: tModal('technicalError'),
  };

  // ---- Validation outcome ---------------------------------------------------

  const settle = useCallback(
    (attempt: number, v: DecodableValidation, settledAngleId: string | null) => {
      if (attempt !== attemptRef.current) return;
      abortRef.current?.abort();
      setWatch(null);
      rawStatusRef.current = v.status ?? 'unknown';

      const startedAt = checkStartedAtRef.current;
      track(AnalyticsEvent.FIRST_RUN_VALIDATION_DONE, {
        status: rawStatusRef.current,
        duration_ms: startedAt ? Date.now() - startedAt : 0,
      });
      // The building's views changed (or a file was filed elsewhere).
      cacheInvalidation.afterCreateProjectAngle(projectId);

      const decoded = decodeValidation(v, copy.current);
      if (decoded.status === 'valid' && settledAngleId) {
        setAngleId(settledAngleId);
        setPhoto((p) => ({ ...p, status: 'valid', message: null, isSlow: false }));
        return;
      }
      const isTechnical = decoded.validation?.is_technical_error === true;
      setPhoto((p) => ({
        ...p,
        status: isTechnical ? 'technical_error' : 'rejected',
        message:
          decoded.error ?? decoded.note ?? copy.current.notSuitable,
        isSlow: false,
        // A technical failure is ours, not the photo's: the same file can be
        // sent again, which is how the upload window repeats it too.
        canRetry: isTechnical && fileRef.current !== null,
      }));
    },
    [projectId]
  );

  const timeOut = useCallback((attempt: number) => {
    if (attempt !== attemptRef.current) return;
    rawStatusRef.current = 'timeout';
    track(AnalyticsEvent.FIRST_RUN_VALIDATION_DONE, {
      status: 'timeout',
      duration_ms: checkStartedAtRef.current
        ? Date.now() - checkStartedAtRef.current
        : 0,
    });
    setPhoto((p) => ({ ...p, status: 'timeout', isSlow: false }));
  }, []);

  /** Wait for a verdict: the socket usually answers, the poll is the floor. */
  const startWatching = useCallback(
    (next: Watch) => {
      const attempt = attemptRef.current;
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      checkStartedAtRef.current = Date.now();
      rawStatusRef.current = 'processing';
      setWatch(next);
      setPhoto((p) => ({ ...p, status: 'checking', isSlow: false, progress: null }));

      if (next.kind === 'intake') {
        pollUploadIntake(projectId, next.uploadId, controller.signal)
          .then((result) => {
            if (!result) timeOut(attempt);
            else
              settle(
                attempt,
                result.validation as unknown as DecodableValidation,
                result.angleId
              );
          })
          .catch((error) => {
            if (!controller.signal.aborted) logger.warn('Intake poll failed', error);
          });
        return;
      }

      // A view that already exists and is still being read (resume). Same
      // cadence and budget as the intake poll.
      void (async () => {
        try {
          await sleep(VALIDATION_POLL_INITIAL_DELAY_MS, controller.signal);
          for (let waited = 0; waited < VALIDATION_WAIT_TIMEOUT_MS; waited += VALIDATION_POLL_INTERVAL_MS) {
            try {
              const angle = await anglesService.getAngle(next.angleId);
              const v = angle.attributes.validation;
              if (!v || v.is_in_progress === false) {
                settle(attempt, v ?? { status: 'valid' }, next.angleId);
                return;
              }
            } catch (error) {
              if (controller.signal.aborted) return;
              logger.warn('View poll failed', error);
            }
            await sleep(VALIDATION_POLL_INTERVAL_MS, controller.signal);
          }
          timeOut(attempt);
        } catch {
          // Aborted: a newer attempt or the socket took over.
        }
      })();
    },
    [projectId, settle, timeOut]
  );

  // Socket fast path for a fresh intake.
  useEffect(() => {
    if (watch?.kind !== 'intake') return;
    const attempt = attemptRef.current;
    return onInstant('uploads', (event) => {
      if (event.action !== 'completed' || event.id !== watch.uploadId) return;
      const settled = readUploadCompletedPayload(event.payload);
      if (settled) settle(attempt, settled.validation, settled.angleId ?? null);
    });
  }, [watch, settle]);

  // After FIRST_RUN_SLOW_AFTER_MS of checking, the status line says so.
  useEffect(() => {
    if (photo.status !== 'checking' || photo.isSlow) return;
    const elapsed = Date.now() - (checkStartedAtRef.current ?? Date.now());
    const timer = setTimeout(
      () => setPhoto((p) => (p.status === 'checking' ? { ...p, isSlow: true } : p)),
      Math.max(0, FIRST_RUN_SLOW_AFTER_MS - elapsed)
    );
    return () => clearTimeout(timer);
  }, [photo.status, photo.isSlow]);

  // ---- Photo ------------------------------------------------------------------

  const uploadFile = useCallback(
    async (file: File) => {
      const attempt = ++attemptRef.current;
      abortRef.current?.abort();
      setWatch(null);
      setAngleId(null);
      fileRef.current = file;
      rawStatusRef.current = 'uploading';
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
      const previewUrl = URL.createObjectURL(file);
      blobUrlRef.current = previewUrl;
      setPhoto({ ...IDLE_PHOTO, status: 'uploading', progress: 0, previewUrl });

      try {
        // The cropper's default frame, applied without the cropper: same
        // rect, ratio, size policy and byte budget as confirming it untouched.
        const cropped = await autoCropImageFile(file, 'angle');
        if (attempt !== attemptRef.current) return;
        const croppedUrl = URL.createObjectURL(cropped);
        URL.revokeObjectURL(previewUrl);
        blobUrlRef.current = croppedUrl;
        setPhoto((p) => ({ ...p, previewUrl: croppedUrl }));

        const result = await uploadSingle(
          cropped,
          'angle',
          (processed) =>
            registerAngleIntake(projectId, processed, { intent: 'first_run' }),
          (id) => uploadsService.confirm(projectId, id)
        );
        if (attempt !== attemptRef.current) return;
        startWatching({ kind: 'intake', uploadId: result.id });
      } catch (error) {
        if (attempt !== attemptRef.current) return;
        logger.error('First-run upload failed', error);
        // Typed limits open the server paywall; the photo stays failed below
        // so the user can pick another one afterwards.
        routeToPaywall(error);
        rawStatusRef.current = 'upload_failed';
        setPhoto((p) => ({
          ...p,
          status: 'upload_failed',
          progress: null,
          message: localizeUploadError(error, tUpload, tAngles('uploadFailed')),
        }));
      }
    },
    [projectId, uploadSingle, startWatching, routeToPaywall, tUpload, tAngles]
  );

  /** A file was picked on Screen 1: go to the choice at once, upload behind it. */
  const pickFile = useCallback(
    (file: File) => {
      track(AnalyticsEvent.FIRST_RUN_PHOTO_SELECTED, { source: 'file' });
      setLaunchError(null);
      setStep('choose');
      void uploadFile(file);
    },
    [uploadFile]
  );

  const retry = useCallback(() => {
    if (fileRef.current) void uploadFile(fileRef.current);
  }, [uploadFile]);

  const waitMore = useCallback(() => {
    if (watch) startWatching(watch);
  }, [watch, startWatching]);

  // Resume on Screen 2: read the photo the server named and its current state.
  const resumedRef = useRef(false);
  useEffect(() => {
    if (resumedRef.current) return;
    resumedRef.current = true;
    track(AnalyticsEvent.FIRST_RUN_SHOWN, {
      step: resolveInitialStep(state),
      is_resume: state.resume_step === 'choose',
    });
    if (resolveInitialStep(state) !== 'choose' || !state.angle_id) return;

    const resumeAngleId = state.angle_id;
    const attempt = ++attemptRef.current;
    setPhoto({ ...IDLE_PHOTO, status: 'checking' });
    anglesService
      .getAngle(resumeAngleId)
      .then((angle) => {
        if (attempt !== attemptRef.current) return;
        setPhoto((p) => ({ ...p, previewUrl: angle.attributes.file?.url ?? null }));
        const v = angle.attributes.validation;
        if (v && v.is_in_progress !== false && v.status !== 'valid') {
          startWatching({ kind: 'angle', angleId: resumeAngleId });
          return;
        }
        checkStartedAtRef.current = Date.now();
        settle(attempt, v ?? { status: 'valid' }, resumeAngleId);
      })
      .catch((error) => {
        if (attempt !== attemptRef.current) return;
        logger.warn('Resume photo unavailable', error);
        // The named photo is gone: start over from the upload screen.
        setPhoto(IDLE_PHOTO);
        setStep('upload');
      });
  }, [state, settle, startWatching]);

  useEffect(
    () => () => {
      abortRef.current?.abort();
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
    },
    []
  );

  // ---- Choice and launch -------------------------------------------------------

  const makeChoice = useCallback(
    (next: FirstRunChoice) => {
      setLaunchError(null);
      setChoice(next);
      track(AnalyticsEvent.FIRST_RUN_CHOICE_MADE, {
        choice: next.kind,
        text_length: next.kind === 'custom' ? next.text.trim().length : 0,
        before_validation: photo.status !== 'valid',
      });
    },
    [photo.status]
  );

  const launch = useCallback(
    async (launchChoice: FirstRunChoice, launchAngleId: string) => {
      launchingRef.current = true;
      setIsLaunching(true);

      let target;
      try {
        target =
          (await findResumableDraftForAngle(projectId, launchAngleId)) ??
          (await loadMainConceptAngle(
            (
              await createConcept.mutateAsync({
                projectId,
                mainAngleId: launchAngleId,
              })
            ).id
          ));
      } catch (error) {
        // No design to land on: stay here and let the user try again.
        logger.error('First-run concept setup failed', error);
        launchingRef.current = false;
        setIsLaunching(false);
        setChoice(null);
        setLaunchError(
          error instanceof ApiClientError ? error.getFirstError() : tModal('technicalError')
        );
        return;
      }

      let conceptId = target.conceptId;
      try {
        const render = await rendersService.createRender(
          target.conceptId,
          buildFirstRunRenderRequest(launchChoice),
          target.conceptAngleId,
          // One wizard launch = one order, however often it is repeated.
          { idempotencyKey: `first-run:${launchAngleId}` }
        );
        // A main render may land on a new concept; follow it there.
        conceptId =
          render.relationships?.concept_angle?.meta?.concept_id ?? target.conceptId;
        trackEvent(AnalyticsEvent.RENDER_CREATED, {
          mode: 'explore',
          angle_kind: 'main',
          tokens_balance: tokensBalance,
        });
        track(AnalyticsEvent.FIRST_RUN_RENDER_STARTED, { choice: launchChoice.kind });
        markFirstRunFinished();
      } catch (error) {
        track(AnalyticsEvent.FIRST_RUN_RENDER_BLOCKED, {
          reason: renderErrorBlockReason(error) ?? 'render_error',
          code: error instanceof ApiClientError ? (error.code ?? String(error.status)) : 'unknown',
        });
        // Keep the idea: the concept page opens with it in the Explore prompt.
        const text =
          launchChoice.kind === 'custom' ? normalizeCustomText(launchChoice.text) : null;
        if (text) {
          saveDraft(
            target.conceptId,
            undefined,
            undefined,
            'explore',
            { prompts: { prompt_concept: text, prompt_angle: '' } },
            {}
          );
        }
        setHandoff({ conceptId: target.conceptId, error });
      }
      router.replace(`/project/${projectId}/concept/${conceptId}`);
    },
    [projectId, createConcept, router, setHandoff, tModal, tokensBalance]
  );

  // Photo checked + choice made → launch, with no extra screen.
  useEffect(() => {
    if (photo.status === 'valid' && angleId && choice && !launchingRef.current) {
      void launch(choice, angleId);
    }
  }, [photo.status, angleId, choice, launch]);

  // ---- Leaving ----------------------------------------------------------------

  const leave = useCallback(() => {
    router.replace(`/project/${projectId}`);
  }, [router, projectId]);

  const abandon = useCallback(() => {
    track(AnalyticsEvent.FIRST_RUN_ABANDONED, {
      step,
      validation_status: rawStatusRef.current,
    });
    leave();
  }, [step, leave]);

  const skip = useCallback(() => {
    track(AnalyticsEvent.FIRST_RUN_SKIPPED, { step });
    markFirstRunFinished();
    firstRunService.dismiss().catch((error) => {
      // The latch is the server's; a lost call only means the wizard may be
      // offered again next session.
      logger.warn('first-run-dismissed failed', error);
    });
    leave();
  }, [step, leave]);

  /** Screen 2 back: return to the photo step, keeping the typed idea. */
  const backToUpload = useCallback(() => {
    setLaunchError(null);
    setStep('upload');
  }, []);

  return {
    step,
    photo,
    choice,
    customText,
    setCustomText,
    isLaunching,
    launchError,
    pickFile,
    retry,
    waitMore,
    makeChoice,
    backToUpload,
    abandon,
    skip,
  };
}
